import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { ScanLine, Keyboard, CheckCircle2, XCircle, Loader2, Camera, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, img, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDate, offerText } from "@/lib/format";
import { PageHead } from "@/components/portal/PortalShell";
import { Empty, NativeSelect, StatusBadge, Field } from "@/components/site/bits";
import { BranchFields, emptyBranch } from "@/pages/SellerRegister";

const useBranches = () => {
  const [branches, setBranches] = useState([]);
  const load = () => api.get("/seller/branches").then((r) => setBranches(r.data));
  useEffect(() => {
    load();
  }, []);
  return [branches, load];
};

export function SellerBranches() {
  const { user } = useAuth();
  const owner = user.merchant_role === "owner";
  const [branches, load] = useBranches();
  const [edit, setEdit] = useState(null);
  const save = async () => {
    try {
      const body = { ...edit, lat: +edit.lat, lng: +edit.lng };
      edit.id ? await api.put(`/seller/branches/${edit.id}`, body) : await api.post("/seller/branches", body);
      toast.success("Branch saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const toggle = async (b) => {
    await api.post(`/seller/branches/${b.id}/active`, { active: !b.active });
    load();
  };
  return (
    <div>
      <PageHead title="My Branches" sub="Every deal is published at exactly one branch.">
        {owner && <button data-testid="add-branch-button" onClick={() => setEdit({ ...emptyBranch })} className="px-5 h-10 inline-flex items-center gap-1 rounded-full bg-red-500 text-white font-bold text-sm"><Plus className="w-4 h-4" />Add branch</button>}
      </PageHead>
      <div className="grid md:grid-cols-2 gap-4">
        {branches.map((b) => (
          <div key={b.id} data-testid={`branch-${b.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 space-y-1">
            <div className="flex justify-between items-start"><p className="font-heading font-bold">{b.name}</p><StatusBadge status={b.active ? "active" : "suspended"} /></div>
            <p className="text-sm text-slate-600">{b.address}, {b.city}</p>
            <p className="text-xs text-slate-500">{b.lat}, {b.lng} · {b.phone} · {b.opening_hours}</p>
            {owner && <div className="flex gap-2 pt-2"><button data-testid={`edit-branch-${b.id}`} onClick={() => setEdit(b)} className="btn-act"><Pencil className="w-3.5 h-3.5" />Edit</button><button data-testid={`toggle-branch-${b.id}`} onClick={() => toggle(b)} className="btn-act">{b.active ? "Deactivate" : "Activate"}</button></div>}
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle className="font-heading">{edit?.id ? "Edit branch" : "Add branch"}</DialogTitle></DialogHeader>
          {edit && <BranchFields b={edit} setB={setEdit} />}
          <button data-testid="branch-save-button" onClick={save} className="h-11 rounded-xl bg-red-500 text-white font-bold">Save branch</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function SellerCoupons() {
  const [branches] = useBranches();
  const [status, setStatus] = useState("");
  const [branch, setBranch] = useState("");
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/seller/coupons", { params: { status: status || undefined, branch_id: branch || undefined } }).then((r) => setData(r.data));
  }, [status, branch]);
  return (
    <div>
      <PageHead title="My Coupons" sub="Claims issued for your deals. Customer contact details are never shared." />
      <div className="flex flex-wrap gap-3 mb-4">
        <NativeSelect data-testid="coupons-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="!w-44"><option value="">All statuses</option>{["active", "redeemed", "expired", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}</NativeSelect>
        <NativeSelect data-testid="coupons-branch-filter" value={branch} onChange={(e) => setBranch(e.target.value)} className="!w-56"><option value="">All branches</option>{branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</NativeSelect>
      </div>
      {data && Object.keys(data.branch_stats).length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
          {Object.entries(data.branch_stats).map(([b, s]) => (
            <div key={b} className="bg-white border border-slate-200 rounded-xl p-4 text-sm"><p className="font-bold mb-1">{b}</p><p className="text-slate-600">Active {s.active || 0} · Redeemed {s.redeemed || 0} · Expired {s.expired || 0}</p></div>
          ))}
        </div>
      )}
      {!data ? <div className="h-40 bg-slate-100 rounded-2xl animate-pulse" /> : data.items.length === 0 ? <Empty title="No coupons yet" /> : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]" data-testid="seller-coupons-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Deal</th><th className="p-3">Branch</th><th className="p-3">Customer</th><th className="p-3">Claimed</th><th className="p-3">Deadline</th><th className="p-3">Status</th><th className="p-3">Method</th></tr></thead>
            <tbody>{data.items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100"><td className="p-3 font-semibold">{c.deal_title}</td><td className="p-3">{c.branch_name}</td><td className="p-3">{c.customer_display}</td><td className="p-3">{fmtDate(c.claimed_at, c.timezone)}</td><td className="p-3">{fmtDate(c.deadline, c.timezone)}</td><td className="p-3"><StatusBadge status={c.status} /></td><td className="p-3 uppercase text-xs">{c.redemption_method || "—"}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function QrScanner({ onToken }) {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  const stop = async () => {
    try {
      await ref.current?.stop();
    } catch {}
    setOn(false);
  };
  const start = async () => {
    try {
      ref.current = new Html5Qrcode("qr-reader");
      setOn(true);
      await ref.current.start({ facingMode: "environment" }, { fps: 10, qrbox: 230 }, (text) => {
        stop();
        onToken(text);
      });
    } catch (e) {
      setOn(false);
      toast.error("Camera unavailable. Allow camera access or use the 5-digit code.");
    }
  };
  useEffect(() => () => {
    ref.current?.isScanning && ref.current.stop().catch(() => {});
  }, []);
  return (
    <div className="space-y-3">
      <div id="qr-reader" className={`rounded-2xl overflow-hidden bg-slate-900 ${on ? "min-h-[280px]" : "hidden"}`} />
      {!on ? <button data-testid="start-scan-button" onClick={start} className="w-full h-14 rounded-2xl bg-slate-900 text-white font-bold flex items-center justify-center gap-2"><Camera className="w-5 h-5" />Open camera & scan QR</button>
        : <button data-testid="stop-scan-button" onClick={stop} className="w-full h-11 rounded-xl border border-slate-200 font-bold">Stop camera</button>}
    </div>
  );
}

export function SellerVerify() {
  const [branches] = useBranches();
  const [branch, setBranch] = useState("");
  const [mode, setMode] = useState("qr");
  const [code, setCode] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [options, setOptions] = useState(null);
  const [result, setResult] = useState(null);
  const active = branches.filter((b) => b.active);
  useEffect(() => {
    if (!branch && active[0]) setBranch(active[0].id);
  }, [active, branch]);

  const reset = () => { setPreview(null); setOptions(null); setResult(null); setCode(""); setToken(""); };
  const lookup = async (payload) => {
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/seller/verify/lookup", { branch_id: branch, ...payload });
      if (data.needs_disambiguation) setOptions(data.options);
      else { setOptions(null); setPreview({ ...data.coupon, method: payload.qr_token ? "qr" : "code" }); }
    } catch (e) {
      setResult({ ok: false, msg: errMsg(e) });
    } finally {
      setBusy(false);
    }
  };
  const redeem = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/seller/verify/redeem", { coupon_id: preview.coupon_id, branch_id: branch, method: preview.method });
      setResult({ ok: true, coupon: data.coupon, at: data.redeemed_at });
      setPreview(null);
    } catch (e) {
      setResult({ ok: false, msg: errMsg(e) });
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl">
      <PageHead title="Verify Coupon" sub="Scan the customer's QR code or enter their 5-digit code." />
      <div className="space-y-4">
        <Field label="Redeeming at branch"><NativeSelect data-testid="verify-branch-select" value={branch} onChange={(e) => { setBranch(e.target.value); reset(); }}>{active.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</NativeSelect></Field>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="verify-mode-qr" onClick={() => { setMode("qr"); reset(); }} className={`h-14 rounded-2xl font-bold flex items-center justify-center gap-2 border-2 ${mode === "qr" ? "border-red-500 bg-red-50 text-red-600" : "border-slate-200 bg-white"}`}><ScanLine className="w-5 h-5" />Scan QR Code</button>
          <button data-testid="verify-mode-code" onClick={() => { setMode("code"); reset(); }} className={`h-14 rounded-2xl font-bold flex items-center justify-center gap-2 border-2 ${mode === "code" ? "border-red-500 bg-red-50 text-red-600" : "border-slate-200 bg-white"}`}><Keyboard className="w-5 h-5" />Enter 5-digit code</button>
        </div>
        {result?.ok ? (
          <div data-testid="redeem-success" className="rounded-3xl bg-emerald-500 text-white p-8 text-center space-y-2 animate-rise">
            <CheckCircle2 className="w-16 h-16 mx-auto" />
            <p className="font-heading text-2xl font-extrabold">Coupon redeemed</p>
            <p>{result.coupon.deal_title}</p>
            <p className="text-sm opacity-90">{result.coupon.customer_display} · {result.coupon.branch_name} · {fmtDate(result.at)}</p>
            <p className="text-xs opacity-80">Verified store visit recorded.</p>
            <button data-testid="verify-next-button" onClick={reset} className="mt-3 px-6 h-11 rounded-xl bg-white text-emerald-700 font-bold">Verify next</button>
          </div>
        ) : preview ? (
          <div data-testid="coupon-preview" className="bg-white border-2 border-emerald-400 rounded-3xl p-5 space-y-3">
            <div className="flex gap-3">
              {preview.deal_image && <img src={img(preview.deal_image)} alt="" className="w-20 h-20 rounded-xl object-cover" />}
              <div><StatusBadge status={preview.status} /><p className="font-heading font-bold text-lg">{preview.deal_title}</p><p className="text-sm text-red-500 font-bold">{offerText(preview)}</p></div>
            </div>
            <p className="text-sm text-slate-600">Customer: <b>{preview.customer_display}</b> · Branch: <b>{preview.branch_name}</b></p>
            <p className="text-sm text-slate-600">Valid until {fmtDate(preview.deadline)}</p>
            <div className="flex gap-2">
              <button data-testid="cancel-redeem-button" onClick={reset} className="flex-1 h-12 rounded-xl border border-slate-200 font-bold">Cancel</button>
              <button data-testid="confirm-redeem-button" disabled={busy} onClick={redeem} className="flex-1 h-12 rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Confirm redemption</button>
            </div>
          </div>
        ) : mode === "qr" ? (
          <div className="space-y-3">
            <QrScanner onToken={(t) => lookup({ qr_token: t })} />
            <form onSubmit={(e) => { e.preventDefault(); token && lookup({ qr_token: token }); }} className="flex gap-2">
              <Input data-testid="qr-token-input" placeholder="Or paste scanned QR value" value={token} onChange={(e) => setToken(e.target.value)} />
              <button data-testid="qr-token-submit" className="px-4 rounded-md bg-slate-900 text-white text-sm font-bold">Check</button>
            </form>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); code.length === 5 && lookup({ code }); }} className="bg-white border border-slate-200 rounded-3xl p-6 space-y-4 text-center">
            <input data-testid="code-input" inputMode="numeric" autoFocus maxLength={5} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="•••••" className="w-full text-center font-mono text-5xl font-extrabold tracking-[0.4em] outline-none border-b-2 border-slate-200 focus:border-red-500 py-2" />
            <button data-testid="code-submit-button" disabled={code.length !== 5 || busy} className="w-full h-12 rounded-xl bg-red-500 disabled:bg-slate-300 text-white font-bold flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}Verify code</button>
          </form>
        )}
        {options && (
          <div data-testid="disambiguation" className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-2">
            <p className="text-sm font-bold text-amber-900">Multiple coupons share this code. Ask the customer which deal they claimed:</p>
            {options.map((o) => <button key={o.deal_id} onClick={() => lookup({ code, deal_id: o.deal_id })} className="block w-full text-left p-3 rounded-xl bg-white border border-amber-200 font-semibold text-sm">{o.deal_title}</button>)}
          </div>
        )}
        {result && !result.ok && <div data-testid="redeem-error" className="rounded-2xl bg-red-50 border border-red-200 p-4 flex gap-3 text-red-800"><XCircle className="w-5 h-5 shrink-0" />{result.msg}</div>}
      </div>
    </div>
  );
}
